/* eslint-disable @typescript-eslint/no-explicit-any */
import { cn } from "@/lib/utils";
import apiRoutes from "@/src/apiRoutes";
import { Button } from "@/src/components/Button/Button";
import { Modal } from "@/src/components/Modal/Modal";
import { Label, textVariants } from "@/src/components/Text/Text";
import useUrlState from "@/src/hooks/use-url-state";
import api from "@/src/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isEmpty } from "lodash";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import RaiseDispute from "./RaiseDispute";
import { InitiateTradeInfo } from "../types";
import { useTranslation } from "react-i18next";

interface Props {
  channel: string | undefined;
  data: InitiateTradeInfo | undefined;
  actions: Array<string>;
}

const Actions = ({ channel, data, actions }: Props) => {
  const { t } = useTranslation();
  const trans = (key: string) => t(`app.p2p.viewTrade.${key}`);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [setselectedActon, setSetselectedActon] = useState("");
  const [otherReasonn, setOtherReasonn] = useState("");
  const queryClient = useQueryClient();
  const [modal, setModal] = useUrlState("modal");
  const [paymentLoad, setPaymentLoad] = useState(false);
  const [actionTaken, setActionTaken] = useState("");

  const all_actions: any = {
    cancel: trans("actionButtons.cancel"),
    raise_dispute: trans("actionButtons.raise_dispute"),
    release_coin: trans("actionButtons.release_coin"),
    release_crypto: trans("actionButtons.release_crypto"),
    // paid: 'I have Paid'
  };

  async function handleAction(action: string) {
    if (action === "cancel") {
      setOpen(true);
    } else {
      setActionTaken(action);
      const res = await api.post(`${apiRoutes.trade}${channel}/`, {
        type: action === "release_crypto" ? "release_coin" : action,
      });
      return res.data;
    }
  }

  const { mutate, isLoading } = useMutation({
    mutationFn: handleAction,
    onSuccess: (data) => {
      setPaymentLoad(false);
      if (data.success) {
        if (actionTaken === "release_crypto") {
          navigate("/app/trade/p2p");
        }
        toast.success(data?.info);
        queryClient.invalidateQueries({
          queryKey: ["get_trade_info"],
        });
      }
    },
  });

  async function cancelFn() {
    const res = await api.post(apiRoutes.cancel_trade, {
      type: "p2p",
      reason: !isEmpty(otherReasonn) ? otherReasonn : setselectedActon,
      channel: channel,
    });
    return res.data;
  }

  const { mutate: cancelTrade, isLoading: cancelLoad } = useMutation({
    mutationFn: cancelFn,
    onSuccess: (data) => {
      if (data?.success) {
        toast.success(data?.info);
        setOpen(false);
        navigate("/app/trade/p2p");
        queryClient.invalidateQueries({
          queryKey: ["check_enablers_active_trades"],
        });
      } else {
        toast.warning(data?.info);
      }
    },
  });

  const handleRadioChange = (val: string) => {
    setSetselectedActon(val);
  };

  return (
    <div className="flex justify-between w-full md:mt-10  ">
      <div className="flex gap-3">
        {(!isEmpty(actions) ? actions : data?.states)?.map(
          (item, index) =>
            item !== "paid" && (
              <Button
                variant={
                  item === "cancel"
                    ? "warning"
                    : item === "raise_dispute"
                    ? "amber"
                    : "primary"
                }
                onClick={
                  item === "raise_dispute"
                    ? () => setModal("dispute")
                    : () => mutate(item)
                }
                loading={!paymentLoad && isLoading}
                key={index}
              >
                {all_actions[item]}
              </Button>
            )
        )}
      </div>

      {!data?.dispute_raised &&
        data?.type === "buyer" &&
        !data?.states.includes("raise_dispute") && (
          <Button
            onClick={() => [mutate("make_payment"), setPaymentLoad(true)]}
            loading={paymentLoad}
          >
            Payment Completed
          </Button>
        )}
      <Modal
        open={open}
        setOpen={setOpen}
        setter="state"
        footer={
          <div>
            <Button
              disabled={isEmpty(setselectedActon)}
              onClick={() => cancelTrade()}
              loading={cancelLoad}
            >
              {trans("cancelModal.submitButton")}
            </Button>
          </div>
        }
      >
        <div className="grid gap-3">
          <span className={textVariants({ size: "xxl", font: "semibold" })}>
            {trans("cancelModal.description")}
          </span>
          <div>
            <div className="grid gap-3 md:gap-5">
              {data?.cancel_reasons.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="radio"
                    value={item}
                    id={item}
                    onChange={() => [handleRadioChange(item)]}
                    name="reasons"
                  />
                  <span className={cn(textVariants(), "md:text-lg text-base")}>
                    {item}
                  </span>
                </div>
              ))}
            </div>
            {setselectedActon.includes("Other reasons") && (
              <div className="mt-6">
                <Label>{trans("cancelModal.otherReasons.label")}</Label>
                <textarea
                  className="flex h-10 w-full rounded-md border border-input dark:bg-grey-600 bg-bg-input-field px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 "
                  onChange={(e) => setOtherReasonn(e.target.value)}
                  value={otherReasonn}
                  placeholder={trans("cancelModal.otherReasons.placeholder")}
                />
              </div>
            )}
          </div>
        </div>
      </Modal>
      <RaiseDispute
        open={modal === "dispute"}
        setOpen={(val) => setModal(val ? undefined : "dispute")}
        reasons={data?.dispute_reasons}
      />
    </div>
  );
};

export default Actions;
